/** Date and time rendering, always in the viewer's own timezone. */

const cache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timeZone}|${JSON.stringify(options)}`;
  let existing = cache.get(key);
  if (!existing) {
    existing = new Intl.DateTimeFormat('en-US', { timeZone, ...options });
    cache.set(key, existing);
  }
  return existing;
}

export function formatTime(iso: string, timeZone: string): string {
  return formatter(timeZone, { hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

export function formatLongDateTime(iso: string, timeZone: string): string {
  return formatter(timeZone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

export function formatLongDate(iso: string, timeZone: string): string {
  return formatter(timeZone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));
}

/**
 * A `YYYY-MM-DD` day key from the availability response is a calendar date in
 * the *practice's* timezone, so it is split rather than passed through `Date`,
 * which would shift it into the viewer's zone.
 */
export function splitCalendarDate(date: string): { year: number; month: number; day: number } {
  const [year, month, day] = date.split('-').map(Number);
  return { year: year ?? 1970, month: month ?? 1, day: day ?? 1 };
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function describeCalendarDate(date: string): {
  weekday: string;
  day: string;
  month: string;
} {
  const { year, month, day } = splitCalendarDate(date);
  const weekdayIndex = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

  return {
    weekday: WEEKDAY_LABELS[weekdayIndex] ?? '',
    day: String(day),
    month: MONTH_LABELS[month - 1] ?? '',
  };
}

export function describeDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return `${hours}h ${rest}m`;
}

export function describeLocation(kind: string): string {
  switch (kind) {
    case 'google_meet':
      return 'Google Meet';
    case 'phone':
      return 'Phone call';
    case 'in_person':
      return 'In person';
    default:
      return 'To be confirmed';
  }
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'L';
  }
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
}
